var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import React from 'react';
import FormSection, { isFormSectionChild } from './FormSection';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { isFunction } from '@cvent/carina-utils';
var isDivOnSubmit = function (props, onSubmit) { return props.element === 'div' && isFunction(onSubmit); };
var isDivForm = function (props) { return props.element === 'div'; };
/**
 * Forms have sections, sections have rows, rows have columns.
 */
var Form = /** @class */ (function (_super) {
    __extends(Form, _super);
    function Form() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /**
         * This function is meant to be an event listened to on the form element. Specifically only when the form is element="div"
         *
         * The idea is to mimic the behavior of a real form when the element is a div. This follows the conditions around an
         * implicit form submission (https://www.w3.org/TR/html52/sec-forms.html#implicit-submission), where the user hits the
         * enter key while focusing an input element.
         *
         * @param {KeyboardEvent} event The synthetic keyboard event from onKeyPress
         */
        _this.watchEnterClick = function (event) {
            var _a;
            var key = event.key, target = event.target, currentTarget = event.currentTarget;
            var onSubmit = _this.props.onSubmit;
            // Find a submit button that is a child of the form element.
            var submitButton = currentTarget.querySelector('input[type="submit"], button[type="submit"]');
            // Count the number of fields that block implicit submission.
            var implicitSubmissionInputCount = currentTarget.querySelectorAll('input:not([type]),input[type="text"],input[type="search"],input[type="url"],input[type="tel"],input[type="email"],input[type="password"],input[type="datetime-local"],input[type="date"],input[type="month"],input[type="week"],input[type="time"],input[type="number"]').length;
            if (
            // Only apply logic if the form is a div type form and onSubmit is defined as a function
            isDivOnSubmit(_this.props, onSubmit) &&
                // The enter key is the trigger
                key === 'Enter' &&
                // make sure the target is an element and not something else
                target instanceof Element &&
                // Focused element must be an input
                target.tagName.toLowerCase() === 'input' &&
                // If the form doesn't have a submit button the number of implicit submission blockers must be 1 or less
                (submitButton || implicitSubmissionInputCount <= 1) &&
                // Don't submit if the submit button is disabled
                !(submitButton === null || submitButton === void 0 ? void 0 : submitButton.getAttribute('disabled')) &&
                // Ignore if focused input is the submit button. It will be handled by the onClick
                (!target.getAttribute('type') ||
                    ((_a = target.getAttribute('type')) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== 'submit')) {
                // Pass the event so stopping propagation and preventing default is an option in the onSubmit function
                onSubmit(event);
            }
        };
        /**
         * This function is meant to listen to clicks on the form element. Specifically only when the form is element="div"
         *
         * We check to see if the target element is a submit button and if it is we can submit the div form.
         *
         * @param {ClickEvent} event The synthetic click event from onClick
         */
        _this.watchSubmitClick = function (event) {
            var target = event.target;
            var onSubmit = _this.props.onSubmit;
            if (isDivOnSubmit(_this.props, onSubmit) &&
                // make sure the target is an element and not something else
                target instanceof Element &&
                target.getAttribute('type') === 'submit') {
                // Pass the event so stopping propagation and preventing default is an option in the onSubmit function
                onSubmit(event);
            }
        };
        _this.renderSections = function (_a) {
            var children = _a.children, isRtl = _a.isRtl, stack = _a.stack;
            return React.Children.map(children, function (element) {
                if (isFormSectionChild(element)) {
                    return React.cloneElement(element, { isRtl: isRtl, stack: stack });
                }
                return element;
            });
        };
        _this.handleSubmit = function (event) { return event.preventDefault(); };
        return _this;
    }
    Form.prototype.render = function () {
        if (isDivForm(this.props)) {
            // pull out the props that we don't want to drop: the style related props so they don't clash with our styles and the ones that we use for react stuff, which wouldn't be appropriate for an HTML element
            var _a = this.props, _accessibilityLabel_1 = _a.accessibilityLabel, _element_1 = _a.element, _isRtl_1 = _a.isRtl, _stack_1 = _a.stack, testID_1 = _a.testID, forwardedRef_1 = _a.forwardedRef, _onSubmit = _a.onSubmit, propDrop_1 = __rest(_a, ["accessibilityLabel", "element", "isRtl", "stack", "testID", "forwardedRef", "onSubmit"]);
            return (_jsx("div", __assign({ ref: forwardedRef_1, onKeyPress: this.watchEnterClick, onClick: this.watchSubmitClick }, propDrop_1, injectTestId(testID_1), { css: getContainerStyles(), "aria-label": this.props.accessibilityLabel }, { children: this.renderSections(this.props) }), void 0));
        }
        // pull out the props that we don't want to drop: the style related props so they don't clash with our styles and the ones that we use for react stuff, which wouldn't be appropriate for an HTML element
        var _b = this.props, _accessibilityLabel = _b.accessibilityLabel, _element = _b.element, _isRtl = _b.isRtl, _stack = _b.stack, testID = _b.testID, forwardedRef = _b.forwardedRef, propDrop = __rest(_b, ["accessibilityLabel", "element", "isRtl", "stack", "testID", "forwardedRef"]);
        return (_jsx("form", __assign({ ref: forwardedRef, onSubmit: this.handleSubmit }, propDrop, injectTestId(testID), { css: getContainerStyles(), "aria-label": this.props.accessibilityLabel }, { children: this.renderSections(this.props) }), void 0));
    };
    Form.defaultProps = {
        element: 'form'
    };
    Form.Section = FormSection;
    return Form;
}(React.Component));
function getContainerStyles() {
    return {
        width: '100%',
        position: 'relative'
    };
}
export default Form;
